"use client";

import { useAppDispatch, useAppSelector } from "@/redux/hook";
import StarDetail from "./productRating/StarDetail";
import Tabs from "@/components/Tabs";
import LinkElement from "@/components/LinkElement";
import { CardBadge } from "~components/card";
import WishlistBtn from "~components/wishlist/WishlistBtn";
import {
	CloseIcon,
	EmptyIcon,
	PlusIcon,
	CheckTagIcon,
} from "@/components/icons";
import { ProductJson } from "@/types/Product.type";
import { useMemo, useRef, useState } from "react";
import { cn } from "@/utils/utils";
import Image from "@/components/ui/Image";
import ProductTags from "./ProductTags";
import { setProductCompares } from "@/redux/features/ShopSlice";
import PopupV2 from "@/components/PopupV2";
import InputBorder from "@/components/ui/InputBorder";
import { getListProduct } from "@/lib/api/shop/products";
import { debounce } from "lodash";
import { ChangeEvent } from "react";
import Money from "@/components/ui/Money";
import ProductSinglePrice from "./ProductSinglePrice";
import Helper from "@/utils/helper";
import ProductRating from "./productRating/ProductRating";
import useTranslationApi from "@/lib/hooks/useTranslationApi";
import useCompareProduct from "@/lib/hooks/useCompareProduct";
import ImageConstant from "@/common/constants/image";
import useStoreId from "@/lib/hooks/useStoreId";

export type ProductCompares = {
	product: ProductJson;
	index: number;
};
type DataKeys = {
	label: string;
	key: Key;
};

type DataTableCompare = DataKeys & {
	cols: Col[];
};

type Col = {
	value: string | React.ReactNode;
	isDiff: boolean;
};

type Key = "year" | "brand" | "origin" | "gender" | "perfamer" | "style";

const translate = {
	product_compare: "product_compare",
	only_view_diff: "only_view_diff",
	product_save: "product_save",
	view_detail: "view_detail",
	quick_compare: "quick_compare",
	no_info_product: "no_info_product",
	select_product_to_compare: "select_product_to_compare",
	search_product: "search_product",
	name_search_product: "name_search_product",
	finding: "finding",
	product_brand: "product_brand",
	no_find_product: "no_find_product",
	empty: "empty",
	year_of_release: "year_of_release",
	origin: "origin",
	brand: "brand",
	gender: "gender",
	perfamer: "perfamer",
	style: "style",
};

export default function ProductCompare({ onClose }: { onClose: () => void }) {
	const { t } = useTranslationApi(Object.values(translate));
	const dispatch = useAppDispatch();
	// const store_id = useAppSelector((state) => state.ShopReducer.storeId);
  const {data : store_id} = useStoreId()

	const productCompares = useAppSelector(
		(state) => state.ShopReducer.productCompares
	);
	const { handleCompareProduct } = useCompareProduct();

	const [onlyDiff, setOnlyDiff] = useState(false);
	const [openLookup, setOpenLookup] = useState(false);
	const [isLoopup, setIsLoopup] = useState(false);
	const [loading, setLoading] = useState(false);

	const inputRef = useRef<HTMLInputElement | null>(null);
	const [searchProducts, setSearchProducts] = useState<ProductJson[]>([]);

	const DataKeys: DataKeys[] = [
		{ label: t(translate.year_of_release), key: "year" },
		{ label: t(translate.origin), key: "origin" },
		{ label: t(translate.brand), key: "brand" },
		{ label: t(translate.gender), key: "gender" },

		{ label: t(translate.perfamer), key: "perfamer" },
		{ label: t(translate.style), key: "style" },
	];

	const handleRemove = (product: ProductJson) => {
		// dispatch(setProductCompares({ product: product, type: "remove" }));
		// handleCompareProduct({ product: product, type: "remove", });
	};

	const handleSearchProduct = debounce(async (value: string) => {
		setLoading(true);
		try {
			const res = await getListProduct({
				keyword: value.trim(),
				page: 1,
				limit: 10,
				store_id: store_id,
			});

			setSearchProducts(res.data.items);
		} finally {
			!isLoopup && setIsLoopup(true);
			setLoading(false);
		}
	}, 500);

	const getValueFromKey = (key: Key, data: ProductJson | undefined) => {
		const productView = data;
		if (!productView) {
			return "";
		}
		switch (key) {
			case "brand":
				return productView.brand?.title || "";
			case "origin":
				return productView.origin || "";
			case "year":
				return "2021";
			case "gender":
				return (
					productView.tags.find((t) => t.type === "PRODUCT GENDER")?.value || ""
				);
			case "perfamer":
				const perfamerString = productView.tags
					.filter(
						(t) => t.type === "PRODUCT PERFAMER" && t.name === "Nhóm hương"
					)
					.map((p) => p.value)
					.join(",");
				return perfamerString;
			case "style":
				const styleString = productView.tags
					.filter(
						(t) => t.type === "PRODUCT PERFAMER" && t.name === "Phong cách"
					)
					.map((p) => p.value)
					.join(",");

				return styleString;
			default:
				return "";
		}
	};

	const dataRenderTable: DataTableCompare[] = useMemo(() => {
		const product0 = productCompares.find((pc) => pc.index === 0)?.product;
		const product1 = productCompares.find((pc) => pc.index === 1)?.product;
		const product2 = productCompares.find((pc) => pc.index === 2)?.product;

		let result: DataTableCompare[] = [];
		result = DataKeys.reduce((acc: DataTableCompare[], crr) => {
			const valueFromKeyProduct0 = getValueFromKey(crr.key, product0);
			const valueFromKeyProduct1 = getValueFromKey(crr.key, product1);
			const valueFromKeyProduct2 = getValueFromKey(crr.key, product2);

			acc.push({
				...crr,
				cols: [
					{
						value: valueFromKeyProduct0,
						isDiff:
							valueFromKeyProduct0.replace(/\s/g, "") !==
							valueFromKeyProduct1.replace(/\s/g, ""),
					},
					{
						value: valueFromKeyProduct1,
						isDiff:
							valueFromKeyProduct0.replace(/\s/g, "") !==
							valueFromKeyProduct1.replace(/\s/g, ""),
					},
					{
						value: valueFromKeyProduct2,
						isDiff:
							valueFromKeyProduct0.replace(/\s/g, "") !==
							valueFromKeyProduct1.replace(/\s/g, ""),
					},
				],
			});

			return acc;
		}, []);

		return result;
	}, [DataKeys, productCompares]);

	// const [boxs, setBox] = useState<Box>([]);
	return (
		<div className="w-full h-fit min-h-[60vh] md:min-w-[720px] md:min-h-[580px] relative mt-2 md:mt-0">
			<ul className="flex flex-nowrap w-full">
				<li className="hidden md:inline-block  flex-grow-[0] basis-[300px] w-1/3 flex-shrink-1 border border-gray-200 p-[8px] min-h-[302px] first:border-r-0 last:border-l-0">
					<h5 className="font-[600] text-gray-500 text-[14px]">
						{t(translate.product_compare)}
					</h5>
					<div className="w-full flex flex-col gap-2 mt-4">
						<div className="flex items-center gap-2">
							<input
								checked={onlyDiff}
								onChange={() => {
									setOnlyDiff((prev) => !prev);
								}}
								type="checkbox"
								name="diff"
								id="diff"
							/>
							<label
								className="text-sm leading-[12px] cursor-pointer select-none"
								htmlFor="diff">
								{t(translate.only_view_diff)}
							</label>
						</div>
					</div>
				</li>
				{new Array(3).fill(1).map((i, index) => {
					const productView = productCompares.find(
						(pc) => pc.index === index
					)?.product;
					return (
						<li
							onClick={() => {
								!productView && setOpenLookup(true);
							}}
							key={index}
							className={cn(
								"flex-grow-[0] basis-[300px] w-1/3 flex-shrink-1 border border-gray-300 p-[8px] min-h-[302px] first:border-r-0 last:border-l-0 relative ",
								{ "place-content-center": !productView }
							)}>
							{productView && (
								<div
									onClick={() => {
										handleRemove(productView);
									}}
									className="absolute top-0 right-1/2 translate-x-1/2 -translate-y-1/2 bg-white rounded-md overflow-hidden w-[60px] h-[20px] text-center text-sm z-2 border border-gray-300 cursor-pointer ">
									<CloseIcon className="w-full h-full" />
								</div>
							)}

							{!productView ? (
								<PlusIcon className="text-[32pt] m-auto cursor-pointer w-10 h-10" />
							) : (
								<div className="flex flex-col justify-center items-start h-full">
									<Image
										width={140}
										height={140}
										layout="reponsive"
										src={productView.images?.[0]?.url || ""}
										alt="img"
										className="md:w-[140px] md:h-[140px] w-[120px] h-[120px] md:mt-2 mt-1  flex-shrink-0 mx-auto"
										placeholder={"blur"}
										blurDataURL={ImageConstant.BlurDataURL}
									/>
									<div className="mt-2 flex flex-col flex-1">
										<p className="text-[14px] line-clamp-2 flex-shrink-0">
											{productView.full_name}
										</p>

										<div className=" flex-1    flex flex-col justify-end">
											<ProductSinglePrice
												productVariant={productView}
												showDiscount
												text={`${t(
													translate.product_save
												)} ${Helper.moneyFormat(
													productView.pricePreview
														? productView.compare_discount +
																productView.pricePreview.discount
														: productView.compare_discount
												)}đ - `}
											/>

											<ProductRating item_id={productView.id} defaultRating={productView.rate} />
											<div>
												<LinkElement
													onClick={() => {
														onClose();
													}}
													className="text-blue-500 text-sm"
													href={`/products/${productView.handle}`}>
													{t(translate.view_detail)}
												</LinkElement>
											</div>
										</div>
									</div>

									<CardBadge position="right">
										<WishlistBtn
											model={{
												item_id:
													productView.parent_id > 0
														? productView.parent_id
														: productView.id,
												item_type: 1,
												item_url: productView.handle,
											}}
										/>
									</CardBadge>
									<CardBadge>
										<ProductTags
											tags={productView.tags}
								
											className="flex flex-col gap-1"
											itemClassName=" px-[5px] py-[2px] h-[16px] capitalize"
											type="PRODUCT CARD"
										/>
									</CardBadge>
								</div>
							)}
						</li>
					);
				})}
			</ul>
			<Tabs
				className="mt-2 mb-2"
				tabs={[
					{
						title: t(translate.quick_compare),
						className: "text-base font-[600]",
						content: (
							<>
								{productCompares.length > 0 ? (
									<table className="border border-gray-100 w-full">
										<tbody>
											{dataRenderTable.map((data) => {
												if (
													onlyDiff &&
													!data.cols.every((col) => col.isDiff === true)
												)
													return null;
												return (
													<>
														<tr className="md:hidden md:invisible border text-sm capitalize font-bold">
															<td className="px-2 py-1 text-center" colSpan={2}>
																{data.label}
															</td>
														</tr>
														<tr>
															<td className="w-1/4 px-4 py-2 border text-sm capitalize hidden md:table-cell text-center">
																{data.label}
															</td>
															{data.cols.map((col) => (
																<td
																	className={`w-1/4 p-2 border text-sm text-center ${
																		col.isDiff ? "bg-[#f6fdff]" : ""
																	}`}>
																	{col.value}
																</td>
															))}
														</tr>
													</>
												);
											})}
										</tbody>
									</table>
								) : (
									<div className="w-full h-full min-h-[250px] flex justify-center items-center flex-col gap-2 ">
										<p className="font-[600] text-base">
											{/* Chưa có thông tin sản phẩm */}
											{t(translate.no_info_product)}
										</p>
										<p className="text-sm text-gray-400">
											{/* Bạn hãy chọn sản phẩm để tham khảo thông tin so sánh. */}
											{t(translate.select_product_to_compare)}
										</p>
										<EmptyIcon className="w-10 h-10 text-gray-500" />
									</div>
								)}
							</>
						),
					},
				]}></Tabs>
			<PopupV2
				title={t(translate.search_product)}
				open={openLookup}
				onOpenChange={() => {
					setOpenLookup(false);
				}}
				isDrawerMobile={true}
				classNameContent="md:max-w-[500px]">
				<div className="w-full h-auto min-h-[400px]">
					<input
						ref={inputRef}
						className="border-b border-gray-4 broder-l-0 borer-t-0 border-r-0 p-[2px] text-sm"
						placeholder={t(translate.name_search_product)}
						onChange={(event: ChangeEvent<HTMLInputElement>) =>
							handleSearchProduct(event.target.value)
						}
						name="keyword"></input>
					<div className="flex flex-col gap-2 max-h-[60vh] min-h-[300px] overflow-y-scroll scrollbarCustomer mt-2">
						{loading ? (
							<p className="m-auto">{t(translate.finding)}</p>
						) : searchProducts.length > 0 ? (
							searchProducts.map((p) => (
								<div
									key={p.id}
									className="flex justify-between gap-2 border-b border-gray-300 border-l-0 border-t-0 border-r-0 py-2 first:mt-2">
									<div className="flex gap-2">
										<Image
											src={p.images?.[0]?.url || ""}
											alt="img"
											width={60}
											height={60}
											className="w-[60px] h-[60px]"
											placeholder={"blur"}
											blurDataURL={ImageConstant.BlurDataURL}
										/>
										<div>
											<p className="text-gray-500 text-base line-clamp-2">
												{p.full_name}
											</p>
											<p className="text-sm text-gray-500">
												{t(translate.product_brand)} <b>{p.brand?.title}</b>
											</p>
											<LinkElement
												className="text-blue-400 hover:text-blue-500 transition-colors text-sm cursor-pointer"
												href={`/product/${p.handle}`}>
												{t(translate.view_detail)}
											</LinkElement>
										</div>
									</div>
									<button
										onClick={() => {
											// dispatch(setProductCompares({ product: p, type: "add" }));
											// handleCompareProduct({ product: p, type: "add" });
										}}
										className="basis-[30px] h-[60px] place-content-center shrink-0 ">
										{productCompares.some((pc) => pc.product.id === p.id) ? (
											<CheckTagIcon className="w-5 h-5 text-blue-500" />
										) : (
											<PlusIcon className="w-5 h-5" />
										)}
									</button>
								</div>
							))
						) : (
							<>
								{isLoopup ? (
									<div className="m-auto">
										<p className="font-[500] text-gray-500 text-base">
											{/* Không tìm thấy sản phẩm với nội dung:{" "} */}
											{t(translate.no_find_product)}
											<b>{inputRef?.current?.value}</b>{" "}
										</p>
										<EmptyIcon className="w-10 h-10 mx-auto"></EmptyIcon>
									</div>
								) : (
									<div className="m-auto">
										<p>{t(translate.empty)}</p>
										<EmptyIcon className="w-10 h-10 mx-auto"></EmptyIcon>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</PopupV2>
		</div>
	);
}
